import { useState, useEffect } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints, ApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

interface SubscriptionStatus {
  hasActiveSubscription: boolean;
  subscription: any | null;
  loading: boolean;
  error: string | null;
}

export const useSubscriptionStatus = () => {
  const api = useAuthenticatedApi();
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState<SubscriptionStatus>({
    hasActiveSubscription: false,
    subscription: null,
    loading: true,
    error: null
  });

  useEffect(() => {
    const checkSubscription = async () => {
      if (!api) {
        setStatus(prev => ({ ...prev, loading: false }));
        return;
      }

      try {
        const response = await api.get(apiEndpoints.billing.subscription);
        const subscription = response || null;
        
        setStatus({
          hasActiveSubscription: subscription?.status === 'active' || subscription?.status === 'trial',
          subscription,
          loading: false,
          error: null
        });
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        if (error instanceof ApiError && error.status === 401) {
          // Attempt to refresh local auth state; let guard handle navigation
          checkAuth().catch(() => {});
          setStatus({
            hasActiveSubscription: false,
            subscription: null,
            loading: false,
            error: 'auth'
          });
          return;
        }
        setStatus({
          hasActiveSubscription: false,
          subscription: null,
          loading: false,
          error: 'Failed to verify subscription status'
        });
      }
    };

    checkSubscription();
  }, [api, checkAuth]);

  return status;
}; 